/* eslint-disable max-len */
import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyAutocompleteModule as MatAutocompleteModule } from '@angular/material/legacy-autocomplete';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import { MatLegacyCardModule as MatCardModule } from '@angular/material/legacy-card';
import { MatLegacyCheckboxModule as MatCheckboxModule } from '@angular/material/legacy-checkbox';
import { MatLegacyDialogModule as MatDialogModule } from '@angular/material/legacy-dialog';
import { MatLegacyInputModule as MatInputModule } from '@angular/material/legacy-input';
import { MatLegacyProgressSpinnerModule as MatProgressSpinnerModule } from '@angular/material/legacy-progress-spinner';
import { MatLegacySelectModule as MatSelectModule } from '@angular/material/legacy-select';
import { MatLegacySnackBarModule as MatSnackBarModule } from '@angular/material/legacy-snack-bar';
import { environment } from '../../../../environments/environment';
import { PtlEditorModule } from '../../../editor/editor.module';
import { RestClientService } from '../../../shared/services/rest-client.service';
import { SharedModule } from '../../../shared/shared.module';
import { FrameWorkState } from '../../admin/store/framework.state';
import { MomentTagState } from '../store/moment/moment-tag/moment-tag.state';
import { NewMomentDialogComponent } from './components/new-moment-dialog/new-moment-dialog.component';
import { MomentActionsComponent } from './components/new-moment-section/moment-actions/moment-actions.component';
import { MomentAddTagsComponent } from './components/new-moment-section/moment-add-tags/moment-add-tags.component';
import { MomentDetailsFormComponent } from './components/new-moment-section/moment-details-form/moment-details-form.component';
import { NewMomentExpandBtnComponent } from './components/new-moment-section/new-moment-expand-btn/new-moment-expand-btn.component';
import { NewMomentSectionDialogComponent } from './components/new-moment-section/new-moment-section-dialog/new-moment-section-dialog.component';
import { NewMomentSectionStaticComponent } from './components/new-moment-section/new-moment-section-static/new-moment-section-static.component';
import { FolioNewMomentSectionComponent } from './components/new-moment-section/new-moment-section.component';
import { ApiMomentDataService } from './services/api-data.service';
import { CORE_MOMENT_DATA_SERVICE, MomentDataService } from './services/data.service';
import { MockMomentDataService } from './services/mock-data.service';

import { TranslocoService } from '@ngneat/transloco';
import { NgxsModule } from '@ngxs/store';
import { CONTENT_PACKAGE_DATA_SERVICE } from '../../../shared/services/content-package/content-package-data.service';
import { ApiTagDataService } from '../../../shared/services/tags/api-tag-data.service';
import { MockTagDataService } from '../../../shared/services/tags/mock-tag-data.service';
import { CORE_TAG_DATA_SERVICE, TagDataService } from '../../../shared/services/tags/tag-data.service';
import * as SharedFactoryService from '../../../shared/shared-service-factory';
import { TranslocoRootModule } from '../../../transloco/transloco-root.module';
import { ApiFrameWorkDataService } from '../../admin/services/api-data.service';
import { ADMIN_DATA_SERVICE, AdminDataService } from '../../admin/services/data.service';
import { MockAdminDataService } from '../../admin/services/mock-data.service';
import { PublisherDialogComponent } from '../../publisher/components/publisher-dialog/publisher-dialog.component';

const MATERIAL_MODULES = [
  MatCheckboxModule,
  MatDialogModule,
  MatProgressSpinnerModule,
  MatIconModule,
  MatSelectModule,
  MatAutocompleteModule,
  MatCardModule,
  MatSnackBarModule,
  MatButtonModule,
  MatInputModule,
];

@NgModule({
  declarations: [
    FolioNewMomentSectionComponent,
    MomentDetailsFormComponent,
    NewMomentDialogComponent,
    PublisherDialogComponent,
    MomentActionsComponent,
    MomentAddTagsComponent,
    NewMomentExpandBtnComponent,
    NewMomentSectionDialogComponent,
    NewMomentSectionStaticComponent,
  ],
  imports: [
    CommonModule,
    MATERIAL_MODULES,
    SharedModule,
    PtlEditorModule,
    FormsModule,
    ReactiveFormsModule,
    TranslocoRootModule,
    NgxsModule.forFeature([MomentTagState, FrameWorkState]),
  ],
  providers: [
    { provide: ADMIN_DATA_SERVICE, useFactory: serviceFactory, deps: [RestClientService, TranslocoService] },
    { provide: CORE_TAG_DATA_SERVICE, useFactory: tagServiceFactory, deps: [RestClientService, TranslocoService] },
    {
      provide: CONTENT_PACKAGE_DATA_SERVICE,
      useFactory: SharedFactoryService.contentStoreFactory,
      deps: [RestClientService, TranslocoService],
    },
    {
      provide: CORE_MOMENT_DATA_SERVICE,
      useFactory: momentServiceFactory,
      deps: [RestClientService, TranslocoService],
    },
  ],
  exports: [
    NewMomentDialogComponent,
    NewMomentSectionDialogComponent,
    NewMomentSectionStaticComponent,
    MomentAddTagsComponent,
    NewMomentExpandBtnComponent,
  ],
})
export class SharedMomentModule {}

// -------------------------------------------------------------------------
/** Provides the core moment data service. */
export function momentServiceFactory(client: RestClientService, translocoService: TranslocoService): MomentDataService {
  if (environment.dataProvider === 'mock') {
    return new MockMomentDataService();
  }

  return new ApiMomentDataService(client, translocoService);
}

/** Provides the data service. */
export function serviceFactory(client: RestClientService, translocoService: TranslocoService): AdminDataService {
  if (environment.dataProvider === 'mock') {
    return new MockAdminDataService();
  }

  return new ApiFrameWorkDataService(client, translocoService);
}

export function tagServiceFactory(client: RestClientService, translocoService: TranslocoService): TagDataService {
  if (environment.dataProvider === 'mock') {
    return new MockTagDataService();
  }

  return new ApiTagDataService(client, translocoService);
}
